import { useState, useRef, useEffect } from "react";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  Text,
  SearchField,
  Button,
  ActionButton,
  ProgressCircle,
  IllustratedMessage,
  InlineAlert,
  Heading,
  Content,
} from "@react-spectrum/s2";
import { SyntaxHighlighter } from "@app/components/SyntaxHighlighter";
import AIChat from "@react-spectrum/s2/illustrations/linear/AIChat";
import AddIcon from "@react-spectrum/s2/icons/Add";

import { useAskFluffyJaws } from "@app/hooks/fluffyjaws";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const buildContextualQuery = (history, newQuestion) => {
  if (!history.length) return newQuestion;

  const transcript = history
    .map((m) => `${m.role === "user" ? "User" : "Assistant"}: ${m.text}`)
    .join("\n");

  return `Continue this conversation. Use the prior turns as context and answer the final question.\n\n${transcript}\n\nUser: ${newQuestion}`;
};

export const FluffyJaws = () => {
  useBreadcrumbs([{ label: "Admin", href: "/admin" }, { label: "FluffyJaws" }]);

  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);

  const ask = useAskFluffyJaws();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, ask.isPending]);

  const handleSubmit = (value) => {
    const trimmed = (value ?? query).trim();
    if (!trimmed || ask.isPending) return;

    const contextualQuery = buildContextualQuery(messages, trimmed);

    setMessages((prev) => [...prev, { role: "user", text: trimmed }]);
    setQuery("");

    ask.mutate(
      {
        query: contextualQuery,
        sessionId: sessionId || undefined,
        autoInitialize: !sessionId,
      },
      {
        onSuccess: (data) => {
          if (data?.sessionId) setSessionId(data.sessionId);
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              text: data?.text || "No text content returned.",
            },
          ]);
        },
      },
    );
  };

  const handleReset = () => {
    setSessionId(null);
    setMessages([]);
    setQuery("");
    ask.reset();
  };

  const hasConversation = messages.length > 0;

  return (
    <>
      <title>FluffyJaws | BeStory</title>
      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          gap: 24,
          width: "full",
          maxWidth: 1024,
          height: "calc(100vh - 6.5rem)",
          marginX: "auto",
          paddingX: 24,
          boxSizing: "border-box",
        })}
      >
        <div
          className={style({
            display: "flex",
            alignItems: "center",
            gap: 12,
          })}
        >
          <div className={style({ font: "heading" })}>FluffyJaws</div>
          {hasConversation && (
            <ActionButton
              isQuiet
              onPress={handleReset}
              styles={style({ marginStart: "auto" })}
            >
              <AddIcon />
              <Text>New conversation</Text>
            </ActionButton>
          )}
        </div>

        <div
          ref={scrollRef}
          className={style({
            flexGrow: 1,
            overflowY: "auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          })}
        >
          {!hasConversation && !ask.isPending && (
            <div
              className={style({
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                size: "full",
              })}
            >
              <IllustratedMessage orientation="vertical">
                <AIChat />
                <Heading>Ask FluffyJaws</Heading>
                <Content>
                  Type a question below and press Enter. Follow-up questions
                  stay in the same conversation.
                </Content>
              </IllustratedMessage>
            </div>
          )}

          {messages.map((message, index) => {
            const isUser = message.role === "user";
            return (
              <div
                key={index}
                className={style({
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  width: "fit",
                  maxWidth: "[85%]",
                  boxSizing: "border-box",
                  backgroundColor: "elevated",
                  boxShadow: "elevated",
                  borderRadius: "xl",
                  paddingX: 16,
                  paddingY: 12,
                  alignSelf: {
                    default: "start",
                    isUser: "end",
                  },
                })({ isUser })}
              >
                <Text
                  styles={style({ font: "detail", color: "neutral-subdued" })}
                >
                  {isUser ? "You" : "FluffyJaws"}
                </Text>
                <SyntaxHighlighter language="markdown" code={message.text} />
                {/* <div
                  className={style({
                    font: "body",
                    whiteSpace: "pre-wrap",
                  })}
                >
                  {message.text}
                </div> */}
              </div>
            );
          })}

          {ask.isPending && (
            <div
              className={style({
                display: "flex",
                alignItems: "center",
                gap: 8,
                alignSelf: "start",
                paddingX: 16,
                paddingY: 12,
              })}
            >
              <ProgressCircle
                aria-label="Asking FluffyJaws"
                size="S"
                isIndeterminate
              />
              <Text styles={style({ font: "body", color: "neutral-subdued" })}>
                Thinking...
              </Text>
            </div>
          )}

          {ask.isError && (
            <InlineAlert variant="negative" styles={style({ width: "full" })}>
              <Heading>Failed to ask FluffyJaws</Heading>
              <Content>{ask.error?.message || "Something went wrong."}</Content>
            </InlineAlert>
          )}
        </div>

        <div
          className={style({
            display: "flex",
            gap: 12,
            alignItems: "end",
          })}
        >
          <SearchField
            aria-label="Ask FluffyJaws"
            placeholder={
              sessionId ? "Ask a follow-up..." : "Ask FluffyJaws a question..."
            }
            value={query}
            onChange={setQuery}
            onSubmit={handleSubmit}
            isDisabled={ask.isPending}
            styles={style({ flexGrow: 1 })}
          />
          <Button
            variant="accent"
            isDisabled={ask.isPending || !query.trim()}
            onPress={() => handleSubmit()}
          >
            Ask
          </Button>
        </div>
      </div>
    </>
  );
};
